import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * A level you open INSIDE a screen — a provider's editor inside Settings — kept
 * as a search param and pushed onto browser history, so going back is a stack.
 *
 * Settings switches tabs with `replace`, which is right for siblings: moving
 * between Providers and Models is not somewhere you went. Opening one provider
 * is. When that editor was only component state, both the header's back button
 * and the browser's went straight past Settings to wherever you came from.
 *
 * Opening pushes an entry marked with the param it opened; closing pops that
 * entry when it is there, so Back, Cancel and Save all leave history the same
 * shape. A deep-link straight to the level has nothing of ours to pop, so the
 * param is removed in place instead.
 */
export interface DrillLevel {
  /** The param's value, or null when the level is closed. */
  value: string | null;
  /** The current URL with the level closed. */
  parentPath: string;
  open: (value: string) => void;
  close: () => void;
}

export function useDrillLevel(param: string): DrillLevel {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const value = searchParams.get(param);
  const parentParams = new URLSearchParams(searchParams);
  parentParams.delete(param);
  const qs = parentParams.toString();
  const parentPath = `${location.pathname}${qs ? `?${qs}` : ''}`;

  const open = (next: string) => {
    const p = new URLSearchParams(searchParams);
    p.set(param, next);
    // Swapping one open item for another is still one level deep: replace, and
    // keep whatever marker the entry had (a deep-link has none to pop).
    if (value !== null) setSearchParams(p, { replace: true, state: location.state });
    else setSearchParams(p, { state: { drilledInto: param } });
  };

  const close = () => {
    if (value === null) return;
    if (drilledHere(location.state, param)) {
      navigate(-1);
    } else {
      navigate(parentPath, { replace: true });
    }
  };

  return { value, parentPath, open, close };
}

/**
 * The same stack for a screen with more than one level (Deliver: sites → a
 * site → one of its plans), where "up" can skip a level via a breadcrumb.
 *
 * Each entry remembers how many of our own entries sit beneath it. Going up
 * that many or fewer pops them; going up further than we pushed (a deep-link
 * straight to a plan) replaces in place, since there is nothing of ours to pop.
 */
export interface DrillStack {
  down: (to: string) => void;
  up: (levels: number, to: string) => void;
  /** For in-level changes (a pane switch) that replace the entry: keep this so
   *  the depth survives. */
  state: unknown;
}

export function useDrillStack(): DrillStack {
  const location = useLocation();
  const navigate = useNavigate();
  const depth = (location.state as { drillDepth?: number } | null)?.drillDepth ?? 0;

  return {
    down: (to) => navigate(to, { state: { drillDepth: depth + 1 } }),
    up: (levels, to) => {
      if (levels <= depth) navigate(-levels);
      else navigate(to, { replace: true });
    },
    state: location.state,
  };
}

function drilledHere(state: unknown, param: string): boolean {
  return (state as { drilledInto?: string } | null)?.drilledInto === param;
}
